import { BusinessEntity, BusinessEntityService } from '@/@types/generated'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuth } from './useAuth'
import { useError } from './useError'
import format from '@/lib/format'
import { ApplicationRoles, InternalRoles } from '@/lib/utils'
import { Outlet } from 'react-router'
import { hasRole } from '@/components/Atoms/Permissions'

interface BusinessEntityContextProps {
  readonly businessEntity: BusinessEntity | null
  readonly businessEntities: BusinessEntity[]
  readonly isLoading: boolean
  readonly isRequired: boolean // True if at least one child view requires a business entity

  loadBusinessEntities: (background?: boolean) => Promise<void>
  setRequired: () => () => void // Sets this view as requiring business entity and returns an unmount callback
  selectBusinessEntity: (
    id: BusinessEntity['id'] | null | BusinessEntity
  ) => void
}

const BusinessEntityContext = createContext<BusinessEntityContextProps>(
  undefined!
)

export function useBusinessEntity() {
  const context = useContext(BusinessEntityContext)
  if (context === undefined)
    throw new Error(
      'useBusinessEntity must be used within a BusinessEntityProvider'
    )

  return context
}

export function BusinessEntityProvider({ children }: PropsWithChildren) {
  // Hooks
  const { user } = useAuth()
  const { showError } = useError()

  // State
  const [loading, setLoading] = useState(0)
  const [required, setRequired] = useState(0)
  const [businessEntity, setBusinessEntity] = useState<BusinessEntity | null>(
    null
  )
  const [businessEntities, setBusinessEntities] = useState<BusinessEntity[]>([])

  // Load business entities
  const loadBusinessEntities = useCallback(
    async (background: boolean = false) => {
      // Not logged in, nothing to load, clear
      // ...OR user is internal user, nothing to load either, they have access to _every_ business
      if (!user || user.internalUser) {
        setBusinessEntities([])
        setBusinessEntity(null)
        return
      }

      // User does not have one of the roles the BE API requires, skip
      if (!hasRole([...InternalRoles, ...ApplicationRoles], user)) return

      try {
        if (!background) setLoading((prev) => prev + 1)
        const { businessEntities } = await BusinessEntityService.list()
        setBusinessEntities(
          (businessEntities || []).sort((a, b) =>
            format.CarrierName(a).localeCompare(format.CarrierName(b))
          )
        )

        // If there is only one company, select it
        if (businessEntities?.length === 1)
          setBusinessEntity(businessEntities[0])
      } catch (err: unknown) {
        showError(err, 'Failed to load business entities.')
      } finally {
        if (!background) setLoading((prev) => prev - 1)
      }
    },
    [user, showError]
  )

  // Select the active business entity
  const selectBusinessEntity = useCallback(
    (id: BusinessEntity['id'] | null | BusinessEntity) => {
      // Clear
      if (id === null) setBusinessEntity(null)

      // If a full entity is provided, use it directly
      if (typeof id === 'object') return setBusinessEntity(id)

      // Find in valid business entities
      const entity = businessEntities.find((e) => e.id === id)
      if (!entity) return

      setBusinessEntity(entity)
    },
    [businessEntities]
  )

  const isLoading = useMemo(() => loading > 0, [loading])
  const isRequired = useMemo(() => required > 0, [required])
  const _setRequired = useCallback(() => {
    setRequired((prev) => prev + 1)
    return () => {
      setRequired((prev) => prev - 1)
      setBusinessEntities([])
    }
  }, [])

  // Load on mount, or when user ID or linked business entities change
  useEffect(() => {
    loadBusinessEntities()
  }, [loadBusinessEntities, user?.id])

  return (
    <BusinessEntityContext.Provider
      value={{
        businessEntity,
        businessEntities,
        isLoading,
        isRequired,

        setRequired: _setRequired,
        loadBusinessEntities,
        selectBusinessEntity,
      }}
    >
      {children || <Outlet />}
    </BusinessEntityContext.Provider>
  )
}
